import type {
  AttendanceAuditEvent,
  AttendanceAuditAction,
  AttendanceBulkAction,
  AttendanceCellKey,
  AttendanceCellRecord,
  AttendanceRecordsFilter,
  AttendanceScope,
  AttendanceSubmissionScope,
  AttendanceStudentLinkTarget,
} from './records.ts';
import type {
  AttendanceChronicAbsenceRow,
  AttendanceDivisionBreakdownRow,
  AttendanceMetricSummary,
} from './metrics.ts';
import type {
  AttendanceClassroomConfig,
  AttendanceControlPlaneConfig,
  AttendanceDivisionConfig,
  AttendanceGradeConfig,
  AttendancePeriodConfig,
  AttendanceRosterMembershipConfig,
  AttendanceSchoolCalendarConfig,
} from './control-plane.ts';
import type { AttendanceStatus } from './status.ts';

export type AttendanceOverviewRollingWindow = 7 | 30 | 60 | 90;

export const ATTENDANCE_QUERY_OPERATION_IDS = [
  'getAttendanceCaptureContext',
  'getAttendanceOverview',
  'listAttendanceRecords',
  'getAttendanceStudentHistory',
  'getAttendanceControlPlaneConfig',
  'listAttendanceAuditEvents',
  'getAttendanceImportStatus',
] as const;

export const ATTENDANCE_MUTATION_OPERATION_IDS = [
  'saveAttendanceRecordCell',
  'applyAttendanceBulkAction',
  'submitAttendanceRecordScope',
  'resetAttendanceRecordScope',
  'updateAttendanceControlPlaneConfig',
  'requestAttendanceImportUpload',
  'startAttendanceImport',
  'exportAttendanceRecordsCsv',
  'exportAttendanceContext',
] as const;

export type AttendanceQueryOperationId = (typeof ATTENDANCE_QUERY_OPERATION_IDS)[number];
export type AttendanceMutationOperationId = (typeof ATTENDANCE_MUTATION_OPERATION_IDS)[number];
export type AttendanceCsvExportScope = 'overview-current-date' | 'records-current-filter';
export type AttendanceContextExportContext = 'overview' | 'new-records' | 'teacher-view';
export type AttendanceContextExportFormat = 'csv' | 'pdf';

export type AttendanceCaptureContextInput = AttendanceScope & {
  schoolDate?: string | null;
};

export type AttendanceOverviewInput = AttendanceScope & {
  schoolDate: string;
  rollingSchoolDayWindow?: AttendanceOverviewRollingWindow | null;
};

export type AttendanceControlPlaneConfigInput = AttendanceScope;

export type AttendanceAuditEventsInput = AttendanceScope & {
  limit?: number;
  nextToken?: string | null;
  action?: AttendanceAuditAction | null;
  fromEpochSeconds?: number | null;
  toEpochSeconds?: number | null;
  importId?: string | null;
};

export type AttendanceImportStatusInput = AttendanceScope & {
  importId: string;
};

export type AttendanceRecordCellInput = AttendanceCellKey & {
  status: AttendanceStatus;
  note?: string | null;
  expectedVersion?: number | null;
};

export type AttendanceBulkActionInput = AttendanceRecordsFilter & {
  action: AttendanceBulkAction;
  studentId?: string | null;
  columnSchoolDate?: string | null;
  columnPeriodId?: string | null;
};

export type AttendanceControlPlaneConfigPatch = Partial<{
  divisions: AttendanceDivisionConfig[];
  grades: AttendanceGradeConfig[];
  classrooms: AttendanceClassroomConfig[];
  periods: AttendancePeriodConfig[];
  rosterMemberships: AttendanceRosterMembershipConfig[];
  schoolCalendar: AttendanceSchoolCalendarConfig;
}>;

export type AttendanceControlPlaneConfigUpdateInput = AttendanceScope & {
  patch: AttendanceControlPlaneConfigPatch;
  expectedUpdatedAt?: number | null;
};

export type AttendanceImportUploadRequestInput = AttendanceScope & {
  fileName: string;
  contentType: 'text/csv';
};

export type AttendanceImportStartInput = AttendanceScope & {
  importId: string;
  mode: 'validate-only' | 'apply';
};

export type AttendanceOverviewCsvExportInput = AttendanceScope & {
  scope: 'overview-current-date';
  schoolDate: string;
  rollingSchoolDayWindow?: AttendanceOverviewRollingWindow | null;
  includeNotes?: boolean;
};

export type AttendanceRecordsCsvExportInput = {
  scope: 'records-current-filter';
  recordsFilter: AttendanceRecordsFilter;
  includeNotes?: boolean;
};

export type AttendanceCsvExportInput = AttendanceOverviewCsvExportInput | AttendanceRecordsCsvExportInput;

export type AttendanceStudentHistoryInput = AttendanceScope & {
  studentId: string;
  fromSchoolDate: string;
  toSchoolDate: string;
};

export type AttendanceContextExportInput =
  | (AttendanceScope & {
      context: 'overview';
      format: AttendanceContextExportFormat;
      schoolDate: string;
      rollingSchoolDayWindow?: AttendanceOverviewRollingWindow | null;
      includeNotes?: boolean;
    })
  | {
      context: 'new-records' | 'teacher-view';
      format: AttendanceContextExportFormat;
      recordsFilter: AttendanceRecordsFilter;
      includeNotes?: boolean;
    };

export type AttendanceCaptureContext = {
  config: AttendanceControlPlaneConfig;
  currentSchoolDate: string;
  currentWeekStart: string;
  currentWeekEnd: string;
  firstUnrecordedClassroomId: string | null;
  unrecordedClassroomIds: string[];
  teacherPreview: { previewOnly: true; writesAllowed: false };
};

export type AttendanceOverview = {
  schoolDate: string;
  metrics: AttendanceMetricSummary;
  chronicAbsenceRows: AttendanceChronicAbsenceRow[];
  divisionBreakdown: AttendanceDivisionBreakdownRow[];
  notSubmittedRows: Array<{
    classroomId: string;
    classroomLabel: string;
    teacherName: string | null;
    periodId: string;
    periodLabel: string;
    routeFilter: AttendanceRecordsFilter;
  }>;
};

export type AttendanceRecordsGrid = {
  filter: AttendanceRecordsFilter;
  cells: AttendanceCellRecord[];
  studentLinks: AttendanceStudentLinkTarget[];
  overwriteWarning: string | null;
};

export type AttendanceStudentHistory = {
  studentId: string;
  studentName: string;
  route: string;
  range: {
    fromSchoolDate: string;
    toSchoolDate: string;
  };
  summaryTotals: Record<AttendanceStatus, number> & {
    absenceMetricCount: number;
    attendanceRatePercent: number;
  };
  rows: Array<{
    schoolDate: string;
    classroomId: string;
    classroomLabel: string;
    periodId: string;
    periodLabel: string;
    status: AttendanceStatus;
    note: string | null;
    version: number;
    editable: false;
  }>;
};

export type AttendanceImportStatus = {
  importId: string;
  status: 'pending' | 'running' | 'failed' | 'completed';
  processedRows: number;
  failedRows: number;
  errors: Array<{ rowNumber: number; message: string }>;
};

export type AttendanceAuditEventsResult = {
  events: AttendanceAuditEvent[];
  nextToken: string | null;
};

type AttendanceCsvExportBase = {
  exportId: string;
  scope: AttendanceCsvExportScope;
  fileName: string;
};

export type AttendanceCsvExportResult = AttendanceCsvExportBase &
  (
    | { delivery: 'inline'; csvText: string; downloadUrl?: null }
    | { delivery: 'download-url'; downloadUrl: string; csvText?: null }
  );

type AttendanceContextExportBase = {
  exportId: string;
  context: AttendanceContextExportContext;
  format: AttendanceContextExportFormat;
  fileName: string;
};

export type AttendanceContextExportResult = AttendanceContextExportBase &
  (
    | { delivery: 'inline'; csvText: string; downloadUrl?: null }
    | { delivery: 'download-url'; downloadUrl: string; csvText?: null }
  );

export type AttendanceQueryArgsMap = {
  getAttendanceCaptureContext: { input: AttendanceCaptureContextInput };
  getAttendanceOverview: { input: AttendanceOverviewInput };
  listAttendanceRecords: { input: AttendanceRecordsFilter };
  getAttendanceStudentHistory: { input: AttendanceStudentHistoryInput };
  getAttendanceControlPlaneConfig: { input: AttendanceControlPlaneConfigInput };
  listAttendanceAuditEvents: { input: AttendanceAuditEventsInput };
  getAttendanceImportStatus: { input: AttendanceImportStatusInput };
};

export type AttendanceQueryResultMap = {
  getAttendanceCaptureContext: AttendanceCaptureContext;
  getAttendanceOverview: AttendanceOverview;
  listAttendanceRecords: AttendanceRecordsGrid;
  getAttendanceStudentHistory: AttendanceStudentHistory;
  getAttendanceControlPlaneConfig: AttendanceControlPlaneConfig;
  listAttendanceAuditEvents: AttendanceAuditEventsResult;
  getAttendanceImportStatus: AttendanceImportStatus;
};

export type AttendanceMutationArgsMap = {
  saveAttendanceRecordCell: { input: AttendanceRecordCellInput };
  applyAttendanceBulkAction: { input: AttendanceBulkActionInput };
  submitAttendanceRecordScope: { input: AttendanceSubmissionScope };
  resetAttendanceRecordScope: { input: AttendanceRecordsFilter };
  updateAttendanceControlPlaneConfig: { input: AttendanceControlPlaneConfigUpdateInput };
  requestAttendanceImportUpload: { input: AttendanceImportUploadRequestInput };
  startAttendanceImport: { input: AttendanceImportStartInput };
  exportAttendanceRecordsCsv: { input: AttendanceCsvExportInput };
  exportAttendanceContext: { input: AttendanceContextExportInput };
};

export type AttendanceMutationResultMap = {
  saveAttendanceRecordCell: { cell: AttendanceCellRecord; changed: boolean };
  applyAttendanceBulkAction: { changedCellCount: number; skippedCellCount: number; retainedNotesCount: number };
  submitAttendanceRecordScope: { submittedAt: number; submittedBy: string; submittedCellCount: number };
  resetAttendanceRecordScope: { changedCellCount: number; retainedNotesCount: number };
  updateAttendanceControlPlaneConfig: AttendanceControlPlaneConfig;
  requestAttendanceImportUpload: { importId: string; uploadUrl: string; expiresAt: number };
  startAttendanceImport: AttendanceImportStatus;
  exportAttendanceRecordsCsv: AttendanceCsvExportResult;
  exportAttendanceContext: AttendanceContextExportResult;
};
